import React, { useEffect, useRef, useState } from 'react';
import { Button, Input, InputNumber, Select, Table, Tooltip } from 'antd';
import { DeleteOutlined, InfoCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { ColumnsType } from 'antd/es/table';
import CustomerPicker from '../../../../components/customers/CustomerPicker';
import VariantPicker from '../../../../components/inventory/VariantPicker';
import type { CustomerOptionDto } from '../../../../dto/reference';
import type { ProductVariant } from '../../../../types/pz';
import { formatCurrencyLocalized } from '../../../../utils/currency';
import { getProductSelectionLabel, getProductSelectorDimensions } from '../../../../utils/productSelectorLabel';
import { formatLocalizedQuantity } from '../../../../utils/numberFormatting';
import { formatUnit } from '../../../../utils/units';
import {
  ACTIVE_QUANTITY_UNITS,
  asQuantityUnit,
  MAX_CUSTOMER_PHYSICAL_QUANTITY,
  MAX_CUSTOMER_PRICING_QUANTITY,
  type CommercialQuantityValue,
} from '../../../../utils/commercialQuantity';
import { POLISH_DECIMAL_INPUT_NUMBER_PROPS } from '../../../../utils/polishNumberInput';
import { unitPriceInputProps } from '../../../../utils/money';

import { formatCommercialPriceUnit } from '../../commercialPriceUnit';
import CommercialQuantityEditor from '../../components/CommercialQuantityEditor';
import { ORDER_CURRENCIES } from '../../currencies';
import { getOrderProductDisplayDimensions, getOrderProductDisplayName } from '../../orderProductDisplay';
import { Callout, HiddenValue, KeyValueList, StepSection } from '../OrderProcessParts';
import {
  buildOrderDraftLine,
  getOrderDraftLineValue,
  getOrderDraftTotals,
  hasOrderDraftLineFractionalPieces,
  hasOrderDraftLinePairMismatch,
  isOrderDraftLinePriceValid,
  isOrderDraftLineQuantityValid,
  setOrderDraftLineQuantity,
  type OrderDraftLine,
  type OrderDraftState,
} from './orderDraftModel';
import {
  applyQuantityPairToOrderDraftLine,
  canConvertOrderDraftLine,
  getOrderDraftLineConversion,
  getOrderDraftLineQuantityValue,
} from './orderDraftLineConversion';
import type { OrderDraftEditorController } from './useOrderDraftEditor';
import OrderDraftMoreData, { type OrderDraftMoreDataRights } from './OrderDraftMoreData';
import OrderDraftLogisticsCard from './OrderDraftLogisticsCard';
import './OrderDraft.css';


interface AddRowState {
  variantId?: string;
  variant?: ProductVariant;
  quantity: number | null;
  unit: string;
  unitPrice: number | null;
  /** Price currency picked on the row; null = the customer price list's or the order currency. */
  currency: string | null;
  /** Unit conversion set on the row (the line to be added); reset when the amount or unit changes. */
  converted: CommercialQuantityValue | null;
}

const EMPTY_ADD_ROW: AddRowState = { quantity: null, unit: 'pcs', unitPrice: null, currency: null, converted: null };

export interface OrderDraftEditorProps {
  draft: OrderDraftState;
  /** Absent = read-only (a later step shows the Szkic as it was). */
  editor?: OrderDraftEditorController;
  /** Adding, changing and removing lines (TESTER only on a new order). */
  canEditLines: boolean;
  canViewSalePrices: boolean;
  canEditSalePrices: boolean;
  /** Customer of the order can be chosen only before the first save. */
  isNew: boolean;
  rights: OrderDraftMoreDataRights;
  /** Name for the read-only view and the picker fallback. */
  customerName: string;
  /** Address of the customer card: the default when „Adres dostawy” is empty. */
  customerAddress?: string | null;
  onCustomerChange?: (customer?: CustomerOptionDto) => void;
}

/**
 * Szkic of the ZK card (makieta `sSzkic`): „Dane szkicu” with the customer,
 * the delivery address and the collapsed „Więcej danych”, one row to add a
 * product, the lines with inline quantity and sale price, and the
 * „Ściąga logistyczna” of the selected line and the whole order.
 */
export default function OrderDraftEditor({
  draft,
  editor,
  canEditLines,
  canViewSalePrices,
  canEditSalePrices,
  isNew,
  rights,
  customerName,
  customerAddress,
  onCustomerChange,
}: OrderDraftEditorProps) {
  const { t, i18n } = useTranslation(['orders', 'common', 'products']);
  const readOnly = !editor;
  const linesEditable = !readOnly && canEditLines;
  const [addRow, setAddRow] = useState<AddRowState>(EMPTY_ADD_ROW);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const { header, lines } = draft;
  const selected = lines.find((line) => line.key === selectedKey) ?? lines[0] ?? null;
  const language = i18n.language;
  const money = (value: number, currency: string) => formatCurrencyLocalized(value, currency, language);
  const unitOptions = (piecesOnly: boolean) => (piecesOnly ? ['pcs'] : ACTIVE_QUANTITY_UNITS)
    .map((unit) => ({ value: unit, label: formatUnit(unit, language) }));

  // Makieta: the picked product reads „nazwa + format” (`P[p].name P[p].fmt`). The picker
  // button keeps the name on one line, the dimension sits under it so a long name never hides it.
  const pickedName = addRow.variant ? getProductSelectionLabel(addRow.variant, t('products:catalogPicker.unnamedProduct')) : '';
  const pickedDimensions = addRow.variant ? getProductSelectorDimensions(addRow.variant) : '';

  const addRowReady = Boolean(addRow.variantId)
    && Number(addRow.quantity) > 0
    && (!canEditSalePrices || Number(addRow.unitPrice) > 0);

  const handleVariantPick = (variantId?: string, variant?: ProductVariant) => {
    if (!variantId) {
      setAddRow(EMPTY_ADD_ROW);
      return;
    }
    const fixed = editor?.customerPrices.get(variantId);
    const catalogPrice = Number(variant?.defaultSalePrice);
    const piecesOnly = (variant?.countInPiecesOnly ?? variant?.product?.countInPiecesOnly) === true;
    setAddRow((current) => ({
      ...current,
      variantId,
      variant,
      unit: piecesOnly ? 'pcs' : current.unit,
      currency: null,
      converted: null,
      unitPrice: canEditSalePrices
        ? (fixed?.fixedPrice ?? (Number.isFinite(catalogPrice) && catalogPrice > 0 ? catalogPrice : current.unitPrice))
        : null,
    }));
  };

  // The line the row would add; with a unit conversion set on the row the accepted pair is applied to it.
  const makeAddRowLine = (unitPrice: number | null) => {
    if (!addRow.variantId) return null;
    const fixed = editor?.customerPrices.get(addRow.variantId);
    const line = buildOrderDraftLine({
      variantId: addRow.variantId,
      variant: addRow.variant,
      quantity: Number(addRow.quantity),
      unit: addRow.unit,
      unitPrice,
      currency: addRow.currency || fixed?.currency || header.currency,
    });
    return addRow.converted ? applyQuantityPairToOrderDraftLine(line, addRow.converted) : line;
  };
  const buildAddRowLine = () => {
    if (!editor || !addRowReady) return null;
    return makeAddRowLine(canEditSalePrices ? Number(addRow.unitPrice) : null);
  };
  const addRowCurrency = addRow.currency || (addRow.variantId ? editor?.customerPrices.get(addRow.variantId)?.currency : undefined) || header.currency;
  // Conversion of a product that is counted in more than pieces, once an amount is entered.
  const addRowPreview = addRow.variantId && Number(addRow.quantity) > 0 ? makeAddRowLine(null) : null;
  const addRowConversion = addRowPreview ? getOrderDraftLineConversion(addRowPreview) : null;

  // „Zapisz szkic” adds a complete row left unadded (and refuses a half-filled one): tell the save.
  const setPendingRow = editor?.setPendingRow;
  const customerPrices = editor?.customerPrices;
  useEffect(() => {
    if (!setPendingRow) return;
    setPendingRow({ line: buildAddRowLine(), incomplete: Boolean(addRow.variantId) && !addRowReady });
    // The line is rebuilt only when the row, the customer price list or the currency change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addRow, addRowReady, customerPrices, header.currency, setPendingRow]);
  useEffect(() => () => setPendingRow?.(null), [setPendingRow]);
  // The save added the row to the table: the row starts over. The same product may still be
  // picked again for another line (split over several lines), so the line key decides, not the product.
  const pendingLineKey = editor?.pendingRow?.line?.key;
  useEffect(() => {
    if (pendingLineKey && lines.some((line) => line.key === pendingLineKey)) setAddRow(EMPTY_ADD_ROW);
  }, [lines, pendingLineKey]);

  const handleAdd = () => {
    const line = buildAddRowLine();
    if (!editor || !line) return;
    editor.addLine(line);
    setSelectedKey(line.key);
    setAddRow(EMPTY_ADD_ROW);
  };

  const lineName = (line: OrderDraftLine) => getOrderProductDisplayName(line as never, line.name || '');

  const columns: ColumnsType<OrderDraftLine> = [
    { title: t('orders:process.lines.no'), key: 'no', width: 48, render: (_v, _line, index) => index + 1 },
    {
      title: t('orders:process.lines.product'),
      key: 'product',
      render: (_v, line) => {
        const conversion = getOrderDraftLineConversion(line);
        const canConvert = linesEditable && canConvertOrderDraftLine(line);
        return (
          <div className="zk-draft__identity">
            <strong>{lineName(line)}</strong>
            {getOrderProductDisplayDimensions(line as never) && <small>{getOrderProductDisplayDimensions(line as never)}</small>}
            {(conversion || canConvert) && (
              <div className="zk-draft__conversion" data-testid="zk-draft-line-conversion" onClick={(event) => event.stopPropagation()}>
                    {conversion && conversion.quantity != null && (
                      <span className="zk-draft__conversion-value">= {formatLocalizedQuantity(conversion.quantity, language)} {formatUnit(conversion.unit, language)}</span>
                    )}
                    {canConvert && (
                      <CommercialQuantityEditor
                        triggerOnly
                        triggerLink
                        triggerLabel={conversion ? t('orders:unitConversion.change') : t('orders:unitConversion.configure')}
                        maxPhysicalQuantity={MAX_CUSTOMER_PHYSICAL_QUANTITY}
                        maxPricingQuantity={MAX_CUSTOMER_PRICING_QUANTITY}
                        width={line.width ?? undefined}
                        length={line.length ?? undefined}
                        height={line.height ?? undefined}
                        thickness={line.thickness ?? undefined}
                        countInPiecesOnly={line.countInPiecesOnly === true}
                        physicalUnit={asQuantityUnit(line.unit)}
                        value={getOrderDraftLineQuantityValue(line)}
                        onChange={(next) => editor?.updateLine(line.key, (current) => applyQuantityPairToOrderDraftLine(current, next))}
                      />
                    )}
                  </div>
            )}
          </div>
        );
      },
    },
    {
      title: t('orders:process.lines.quantity'),
      key: 'quantity',
      align: 'right',
      width: 190,
      render: (_v, line) => {
        // Marked before save: the backend would refuse a pair that disagrees with the dimensions.
        const savedLine = editor?.savedLines?.find((saved) => saved.key === line.key);
        const pairMismatch = linesEditable && hasOrderDraftLinePairMismatch(line, savedLine);
        // Whole pieces only (owner 2026-10-06): the save is refused until it is rounded.
        const fractionalPieces = linesEditable && !pairMismatch && hasOrderDraftLineFractionalPieces(line, savedLine);
        return (
          <div className="zk-draft__qty-cell">
            {linesEditable ? (
              <InputNumber
                className="zk-draft__number"
                controls={false}
                size="small"
                min={0}
                max={MAX_CUSTOMER_PHYSICAL_QUANTITY}
                {...POLISH_DECIMAL_INPUT_NUMBER_PROPS}
                status={isOrderDraftLineQuantityValid(line) && !pairMismatch && !fractionalPieces ? undefined : 'error'}
                value={line.quantity == null ? null : Number(line.quantity)}
                addonAfter={formatUnit(line.unit, language)}
                aria-label={t('orders:process.draft.quantityAria', { name: lineName(line) })}
                onClick={(event) => event.stopPropagation()}
                onChange={(value) => editor?.updateLine(line.key, (current) => setOrderDraftLineQuantity(current, value == null ? null : Number(value)))}
                data-testid="zk-draft-line-quantity"
              />
            ) : (
              <span className="zk-num">{formatLocalizedQuantity(Number(line.quantity), language)} {formatUnit(line.unit, language)}</span>
            )}
            {pairMismatch && (
              <div className="zk-draft__qty-error" role="alert" data-testid="zk-draft-line-pair-mismatch">
                {t('orders:process.draft.quantityPairInconsistent')}
              </div>
            )}
            {fractionalPieces && (
              <div className="zk-draft__qty-error" role="alert" data-testid="zk-draft-line-fractional-pieces">
                {t('orders:process.draft.quantityFractionalPieces')}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: t('orders:process.lines.salePrice'),
      key: 'price',
      align: 'right',
      width: 240,
      render: (_v, line) => {
        if (!canViewSalePrices) return <HiddenValue reason="salePrices" />;
        const currency = line.currency || header.currency;
        const priceUnit = formatCommercialPriceUnit('sale', line, language);
        if (linesEditable && canEditSalePrices) {
          return (
            <InputNumber
              className="zk-draft__number"
              controls={false}
              size="small"
              max={1000000}
              {...unitPriceInputProps(line.unitPrice)}
              {...POLISH_DECIMAL_INPUT_NUMBER_PROPS}
              status={isOrderDraftLinePriceValid(line) ? undefined : 'error'}
              value={line.unitPrice ?? null}
              addonAfter={(
                <span className="zk-draft__price-suffix" onClick={(event) => event.stopPropagation()}>
                  <Select
                    size="small"
                    variant="borderless"
                    value={currency}
                    options={ORDER_CURRENCIES.map((code) => ({ value: code, label: code }))}
                    onChange={(next) => editor?.updateLine(line.key, (current) => ({ ...current, currency: next }))}
                    aria-label={t('orders:process.draft.more.currency')}
                    popupMatchSelectWidth={false}
                    data-testid="zk-draft-line-currency"
                  />
                  /{priceUnit}
                </span>
              )}
              aria-label={t('orders:process.draft.priceAria', { name: lineName(line) })}
              onClick={(event) => event.stopPropagation()}
              onChange={(value) => editor?.updateLine(line.key, (current) => ({ ...current, unitPrice: value == null ? 0 : Number(value) }))}
              data-testid="zk-draft-line-price"
            />
          );
        }
        return <span className="zk-num">{money(Number(line.unitPrice || 0), currency)} / {priceUnit}</span>;
      },
    },
    {
      title: t('orders:process.lines.value'),
      key: 'value',
      align: 'right',
      width: 140,
      render: (_v, line) => (canViewSalePrices
        ? <span className="zk-num">{money(getOrderDraftLineValue(line), line.currency || header.currency)}</span>
        : <HiddenValue reason="salePrices" />),
    },
    ...(linesEditable ? [{
      title: '',
      key: 'remove',
      width: 48,
      render: (_v: unknown, line: OrderDraftLine) => (
        <Tooltip title={t('orders:process.draft.remove', { name: lineName(line) })}>
          <Button
            type="text"
            danger
            size="small"
            icon={<DeleteOutlined />}
            aria-label={t('orders:process.draft.remove', { name: lineName(line) })}
            onClick={(event) => {
              event.stopPropagation();
              editor?.removeLine(line.key);
            }}
            data-testid="zk-draft-line-remove"
          />
        </Tooltip>
      ),
    }] : []),
  ];

  const totals = Object.entries(getOrderDraftTotals(lines, header.currency));
  // The address starts as the customer's and follows a customer change as long as the
  // salesperson has not typed their own; typing over it (or clearing it) is theirs to keep.
  const autoAddress = useRef<string>('');
  useEffect(() => {
    if (!editor) return;
    const next = (customerAddress || '').trim();
    const current = header.deliveryLocation.trim();
    // Still the previous customer's address (or nothing): follow the new customer, also to an empty one.
    if ((current === '' || current === autoAddress.current) && current !== next) {
      editor.setHeader({ deliveryLocation: next });
    }
    autoAddress.current = next;
  }, [customerAddress]); // eslint-disable-line react-hooks/exhaustive-deps

  const addressHint = customerAddress
    ? t('orders:process.draft.deliveryAddressFromCustomer', { address: customerAddress })
    : t('orders:process.draft.deliveryAddressEmpty');

  return (
    <div className="zk-grid zk-draft-grid" data-testid="zk-step-DRAFT">
      <StepSection title={t('orders:process.draft.dataTitle')} testId="zk-draft-data">
        {readOnly ? (
          <KeyValueList rows={[
            [t('orders:process.chips.customer'), customerName || '—'],
            [t('orders:process.draft.deliveryAddress'), header.deliveryLocation || customerAddress || '—'],
          ]} />
        ) : (
          <div className="zk-draft__fields">
            <div className="zk-draft__field">
              <label className="zk-draft__label" htmlFor="zk-draft-customer">
                {t('orders:process.chips.customer')} <span className="zk-draft__required">*</span>
              </label>
              <Tooltip title={isNew ? undefined : t('orders:process.draft.customerLocked')}>
                <span id="zk-draft-customer">
                  <CustomerPicker
                    value={header.customerId}
                    disabled={!isNew}
                    fallbackLabel={customerName || undefined}
                    placeholder={t('orders:process.draft.customerPlaceholder')}
                    onChange={(customerId, customer) => {
                      editor.setHeader({ customerId: customerId || null });
                      onCustomerChange?.(customer);
                    }}
                    data-testid="zk-draft-customer"
                  />
                </span>
              </Tooltip>
            </div>
            <div className="zk-draft__field">
              <label className="zk-draft__label" htmlFor="zk-draft-address">{t('orders:process.draft.deliveryAddress')}</label>
              <Input
                id="zk-draft-address"
                value={header.deliveryLocation}
                disabled={!rights.commercial && !isNew}
                placeholder={customerAddress || t('orders:process.draft.deliveryAddressPlaceholder')}
                onChange={(event) => editor.setHeader({ deliveryLocation: event.target.value })}
                data-testid="zk-draft-address"
              />
              <span className="zk-draft__help">{addressHint}</span>
            </div>
          </div>
        )}

        {!readOnly && <OrderDraftMoreData editor={editor} isNew={isNew} rights={rights} />}

        {linesEditable && (
          <>
            <h4 className="zk-section__group-title">{t('orders:process.draft.addTitle')}</h4>
            <div className="zk-draft__add-wrap">
            <div className="zk-draft__add" data-testid="zk-draft-add">
              <div className="zk-draft__field zk-draft__field--product">
                <span className="zk-draft__label">{t('orders:process.lines.product')}</span>
                <VariantPicker
                  value={addRow.variantId}
                  showInventory={false}
                  placeholder={t('orders:process.draft.productPlaceholder')}
                  triggerLabel={pickedName || undefined}
                  onChange={handleVariantPick}
                  data-testid="zk-draft-add-product"
                />
                {pickedDimensions && (
                  <small className="zk-draft__picked-dimensions" data-testid="zk-draft-add-dimensions">{pickedDimensions}</small>
                )}
              </div>
              <div className="zk-draft__field">
                <span className="zk-draft__label">{t('orders:process.lines.quantity')}</span>
                <InputNumber
                  className="zk-draft__number"
                  controls={false}
                  min={0}
                  max={MAX_CUSTOMER_PHYSICAL_QUANTITY}
                  {...POLISH_DECIMAL_INPUT_NUMBER_PROPS}
                  value={addRow.quantity}
                  placeholder="0"
                  aria-label={t('orders:process.lines.quantity')}
                  onChange={(value) => setAddRow((current) => ({ ...current, quantity: value == null ? null : Number(value), converted: null }))}
                  addonAfter={(
                    <Select
                      size="small"
                      variant="borderless"
                      value={addRow.unit}
                      options={unitOptions((addRow.variant?.countInPiecesOnly ?? addRow.variant?.product?.countInPiecesOnly) === true)}
                      onChange={(unit) => setAddRow((current) => ({ ...current, unit, converted: null }))}
                      aria-label={t('orders:process.draft.unit')}
                      popupMatchSelectWidth={false}
                    />
                  )}
                  data-testid="zk-draft-add-quantity"
                />
              </div>
              <div className="zk-draft__field">
                <span className="zk-draft__label">{t('orders:process.lines.salePrice')}</span>
                {canEditSalePrices ? (
                  <InputNumber
                    className="zk-draft__number"
                    controls={false}
                    max={1000000}
                    {...unitPriceInputProps(addRow.unitPrice)}
                    {...POLISH_DECIMAL_INPUT_NUMBER_PROPS}
                    value={addRow.unitPrice}
                    placeholder="0,00"
                    aria-label={t('orders:process.lines.salePrice')}
                    addonAfter={(
                      <span className="zk-draft__price-suffix">
                        <Select
                          size="small"
                          variant="borderless"
                          value={addRowCurrency}
                          options={ORDER_CURRENCIES.map((code) => ({ value: code, label: code }))}
                          onChange={(currency) => setAddRow((current) => ({ ...current, currency }))}
                          aria-label={t('orders:process.draft.more.currency')}
                          popupMatchSelectWidth={false}
                          data-testid="zk-draft-add-currency"
                        />
                        /{formatUnit(addRowPreview?.saleUnit || addRow.unit, language)}
                      </span>
                    )}
                    onChange={(value) => setAddRow((current) => ({ ...current, unitPrice: value == null ? null : Number(value) }))}
                    data-testid="zk-draft-add-price"
                  />
                ) : (
                  <span className="zk-draft__masked"><HiddenValue reason="salePrices" /></span>
                )}
              </div>
              <div className="zk-draft__field">
                <span className="zk-draft__label" aria-hidden="true">&nbsp;</span>
                <Tooltip title={addRowReady ? undefined : t('orders:process.draft.addHint')}>
                  <Button
                    icon={<PlusOutlined />}
                    disabled={!addRowReady}
                    onClick={handleAdd}
                    data-testid="zk-draft-add-button"
                  >
                    {t('orders:process.draft.add')}
                  </Button>
                </Tooltip>
              </div>
            </div>
            </div>
            {addRowPreview && canConvertOrderDraftLine(addRowPreview) && (
              <div className="zk-draft__add-extra" data-testid="zk-draft-add-conversion">
                {addRowConversion && addRowConversion.quantity != null && (
                  <span className="zk-draft__conversion-value">= {formatLocalizedQuantity(addRowConversion.quantity, language)} {formatUnit(addRowConversion.unit, language)}</span>
                )}
                <CommercialQuantityEditor
                  triggerOnly
                  triggerLink
                  triggerLabel={addRowConversion ? t('orders:unitConversion.change') : t('orders:unitConversion.configure')}
                  maxPhysicalQuantity={MAX_CUSTOMER_PHYSICAL_QUANTITY}
                  maxPricingQuantity={MAX_CUSTOMER_PRICING_QUANTITY}
                  width={addRowPreview.width ?? undefined}
                  length={addRowPreview.length ?? undefined}
                  height={addRowPreview.height ?? undefined}
                  thickness={addRowPreview.thickness ?? undefined}
                  countInPiecesOnly={addRowPreview.countInPiecesOnly === true}
                  physicalUnit={asQuantityUnit(addRowPreview.unit)}
                  value={getOrderDraftLineQuantityValue(addRowPreview)}
                  onChange={(next) => setAddRow((current) => ({ ...current, quantity: next.physicalQuantity, unit: next.physicalUnit, converted: next }))}
                />
              </div>
            )}
          </>
        )}

        <h4 className="zk-section__group-title">{t('orders:process.draft.linesTitle')}</h4>
        {lines.length === 0 ? (
          <Callout tone="muted" testId="zk-draft-empty">{t('orders:process.draft.empty')}</Callout>
        ) : (
          <>
            <Table<OrderDraftLine>
              size="small"
              rowKey="key"
              pagination={false}
              dataSource={lines}
              columns={columns}
              tableLayout="fixed"
              scroll={{ x: 820 }}
              rowClassName={(line) => (line.key === selected?.key ? 'zk-draft__row is-selected' : 'zk-draft__row')}
              onRow={(line) => ({ onClick: () => setSelectedKey(line.key) })}
              data-testid="zk-draft-lines"
            />
            <div className="zk-total" data-testid="zk-draft-total">
              <span>{t('orders:process.lines.totalNet')}</span>
              <strong>
                {canViewSalePrices
                  ? (totals.length ? totals.map(([currency, amount]) => money(amount, currency)).join(' / ') : money(0, header.currency))
                  : <HiddenValue reason="salePrices" />}
              </strong>
            </div>
          </>
        )}
        {readOnly && lines.length > 0 && (
          <p className="zk-table-note"><InfoCircleOutlined aria-hidden="true" /> {t('orders:process.draft.readOnlyNote')}</p>
        )}
      </StepSection>
      <OrderDraftLogisticsCard line={selected} lines={lines} />
    </div>
  );
}
